import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { Dropdown } from "components/Dropdown/Dropdown"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { TimelineScreening } from "interface/patientType"
import { useSupportRequests } from "modules/support/useSupportRequests"
import { useMemo, useRef } from "react"
import { useNavigate, useParams } from "react-router"
import { usePatientDocument } from "services/billings"
import { usePatientDetails, usePatientTimeline } from "services/patients"
import { useUserRole } from "services/users"
import { triggerDownload } from "utils/file"
import { PatientProfile } from "./PatientProfile"
import { PatientSummary } from "./PatientSummary"
import { PatientTimeline } from "./PatientTimeline"
import { TimeManagement } from "./TimeManagement"

export function PatientDashboard() {
	const targetScroll = useRef<HTMLDivElement>(null)
	const { data: role } = useUserRole()
	const { scopeId, userId: patientId } = useParams()
	const navigate = useNavigate()
	const supportRequests = useSupportRequests()
	const { data: patient } = usePatientDetails(patientId)
	const {
		data: { pages: items = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
	} = usePatientTimeline({ patientId })
	const isLoading = isFetching || isFetchingNextPage
	const latestScreening = useMemo(
		() =>
			items.find(({ itemType }) => itemType === "screening")
				?.data as TimelineScreening,
		[items],
	)

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	if (!role || !scopeId || !patientId || !patient) {
		return null
	}

	return (
		<Spinner spinning={isLoading}>
			<div className="mx-auto flex h-full max-h-full flex-col gap-6 overflow-hidden bg-white">
				<div className="flex justify-between">
					<BackButton onClick={() => navigate(`/${scopeId}`)}>
						All patients
					</BackButton>

					{role.isProvider && (
						<div className="flex flex-wrap justify-end gap-3">
							{supportRequests.button}
							<ExportDocuments patientId={patientId} />
							<TimeManagement patientId={patientId} />
						</div>
					)}
				</div>

				<div
					ref={targetScroll}
					className="flex w-full flex-col overflow-y-auto rounded-xl bg-white pr-0.5"
				>
					<div className="@container flex w-full flex-col items-center gap-5 lg:gap-8">
						<div className="flex w-full flex-col gap-5">
							<PatientProfile />

							<PatientSummary latestScreening={latestScreening} />
						</div>

						<PatientTimeline
							items={items}
							patient={patient}
							isAnonymous={role.isProviderAnon}
						/>
					</div>
				</div>
			</div>
		</Spinner>
	)
}

function ExportDocuments({ patientId }: { patientId: string }) {
	const { notify } = useNotification()
	const { mutateAsync: fetchDoc, isPending } = usePatientDocument(patientId)

	return (
		<Dropdown
			classNames={{
				root: "select-none",
			}}
			trigger={["click"]}
			menu={{
				"aria-label": "Export options",
				onClick: async ({ key, domEvent }) => {
					await fetchDoc(key, {
						onSuccess: async ({ blob, filename }) => {
							await triggerDownload(
								blob,
								domEvent.shiftKey ? filename : undefined,
							)
						},
						onError: (error) => {
							notify.error({
								message: error.message,
							})
						},
					})
				},
				items: [
					{
						key: "medical-necessity",
						label: "Medical necessity form",
					},
					{
						key: "patient-monthly-report",
						label: "Previous month's patient monthly report",
					},
					{
						key: "rpm-consent-form",
						label: "Remote patient monitoring consent form",
					},
					{
						key: "rpm-onboarding-report",
						label: "Remote patient monitoring onboarding report",
					},
				],
			}}
		>
			<Button loading={isPending}>Export</Button>
		</Dropdown>
	)
}
